'use client'

import { cn } from '@/lib/utils'
import { Annotation } from '@/types/annotation'
import { Annotation2 } from '@/types/annotation'
import { format, isWithinInterval } from 'date-fns'
import { useEffect, useState } from 'react'
import { FC } from 'react'
import {
  WiDaySunny,
  WiNightClear,
  WiDayCloudy,
  WiNightCloudy,
  WiCloudy,
  WiDayFog,
  WiNightFog,
  WiDayRain,
  WiNightRain,
  WiSnow,
  WiSleet,
  WiRain,
  WiThunderstorm,
  WiDayThunderstorm,
  WiNightThunderstorm,
  WiSnowWind,
  WiStormShowers,
  WiSnowflakeCold,
  WiDaySnowThunderstorm,
  WiDaySnowWind,
  WiDaySnow,
  WiNightAltSnowThunderstorm,
  WiNightAltSnowWind,
  WiNightAltSnow,
  WiNightSnowThunderstorm,
  WiNightSnowWind,
  WiNightSnow,
  WiNightCloudyHigh,
  WiDayCloudyHigh,
  WiNightAltPartlyCloudy
} from 'weather-icons-react'

interface WeatherAtLocation {
  latitude: number
  longitude: number
  generationtime_ms: number
  utc_offset_seconds: number
  timezone: string
  timezone_abbreviation: string
  elevation: number
  current_units: {
    time: string
    interval: string
    temperature_2m: string
  }
  current: {
    time: string
    interval: number
    temperature_2m: number
  }
  hourly_units: {
    time: string
    temperature_2m: string
  }
  hourly: {
    time: string[]
    temperature_2m: number[]
    weather_code: number[]
  }
  daily_units: {
    time: string
    sunrise: string
    sunset: string
  }
  daily: {
    time: string[]
    sunrise: string[]
    sunset: string[]
  }
}

function n(num: number): number {
  return Math.ceil(num)
}

export const Weather: FC<{ weatherAtLocation: any }> = ({ weatherAtLocation }) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!weatherAtLocation) return null

  const currentHigh = Math.max(...weatherAtLocation.hourly.temperature_2m.slice(0, 24))
  const currentLow = Math.min(...weatherAtLocation.hourly.temperature_2m.slice(0, 24))

  const isDay = isWithinInterval(new Date(weatherAtLocation.current.time), {
    start: new Date(weatherAtLocation.daily.sunrise[0]),
    end: new Date(weatherAtLocation.daily.sunset[0])
  })

  const hoursToShow = isMobile ? 5 : 6

  // Find the index of the current time or the next closest time
  const currentTimeIndex = weatherAtLocation.hourly.time.findIndex(
    (time: string | number | Date) => new Date(time) >= new Date(weatherAtLocation.current.time)
  )

  // Slice the arrays to get the desired number of items
  const displayTimes = weatherAtLocation.hourly.time.slice(
    currentTimeIndex,
    currentTimeIndex + hoursToShow
  )
  const displayTemperatures = weatherAtLocation.hourly.temperature_2m.slice(
    currentTimeIndex,
    currentTimeIndex + hoursToShow
  )
  const displayWeatherCodes = (weatherAtLocation.hourly.weather_code || []).slice(
    currentTimeIndex,
    currentTimeIndex + hoursToShow
  )

  const getWeatherIcon = (isDay: boolean, weatherCode: number, temp: number, isHourly = false) => {
    const iconSize = isHourly ? 35 : 42
    const iconColor = '#ffffff'
    const isBitterCold = temp <= -10

    switch (weatherCode) {
      case 0:
        if (isBitterCold) return <WiSnowflakeCold size={iconSize} color={iconColor} />
        return isDay ? (
          <WiDaySunny size={iconSize} color={iconColor} />
        ) : (
          <WiNightClear size={iconSize} color={iconColor} />
        )

      // Partly cloudy
      case 1:
        return isDay ? (
          <WiDayCloudyHigh size={iconSize} color={iconColor} />
        ) : (
          <WiNightAltPartlyCloudy size={iconSize} color={iconColor} />
        )

      case 2:
        return isDay ? (
          <WiDayCloudy size={iconSize} color={iconColor} />
        ) : (
          <WiNightCloudy size={iconSize} color={iconColor} />
        )

      // Overcast
      case 3:
        return isDay ? (
          <WiDayCloudy size={iconSize} color={iconColor} />
        ) : (
          <WiNightCloudyHigh size={iconSize} color={iconColor} />
        )

      // Fog
      case 45:
      case 48:
        return isDay ? (
          <WiDayFog size={iconSize} color={iconColor} />
        ) : (
          <WiNightFog size={iconSize} color={iconColor} />
        )

      // Drizzle
      case 51:
      case 53:
      case 55:
        return temp <= 0 ? (
          <WiSnow size={iconSize} color={iconColor} />
        ) : isDay ? (
          <WiDayRain size={iconSize} color={iconColor} />
        ) : (
          <WiNightRain size={iconSize} color={iconColor} />
        )

      // Freezing Drizzle
      case 56:
      case 57:
        return <WiSleet size={iconSize} color={iconColor} />

      // Rain
      case 61:
        return <WiRain size={iconSize} color={iconColor} /> // Light
      case 63:
        return <WiRain size={iconSize} color={iconColor} /> // Moderate
      case 65:
        return <WiRain size={iconSize} color={iconColor} /> // Heavy

      // Freezing Rain
      case 66:
      case 67:
        return <WiSleet size={iconSize} color={iconColor} />

      // Snow (light, moderate, heavy)
      case 71:
        return isDay ? (
          isBitterCold ? (
            <WiDaySnowWind size={iconSize} color={iconColor} />
          ) : (
            <WiDaySnow size={iconSize} color={iconColor} />
          )
        ) : isBitterCold ? (
          <WiNightSnowWind size={iconSize} color={iconColor} />
        ) : (
          <WiNightSnow size={iconSize} color={iconColor} />
        )
      case 73:
      case 75:
        return isDay ? (
          <WiDaySnowWind size={iconSize} color={iconColor} />
        ) : (
          <WiNightSnowWind size={iconSize} color={iconColor} />
        )

      // Snow grains
      case 77:
        return isDay ? (
          <WiDaySnow size={iconSize} color={iconColor} />
        ) : (
          <WiNightAltSnow size={iconSize} color={iconColor} />
        )

      // Rain showers
      case 80:
      case 81:
      case 82:
        return <WiStormShowers size={iconSize} color={iconColor} />

      // Snow showers
      case 85:
        return isDay ? (
          <WiDaySnow size={iconSize} color={iconColor} />
        ) : (
          <WiNightSnow size={iconSize} color={iconColor} />
        )
      case 86:
        return isDay ? (
          <WiDaySnowWind size={iconSize} color={iconColor} />
        ) : (
          <WiNightSnowWind size={iconSize} color={iconColor} />
        )

      // Thunderstorm with snow
      case 95:
        return isDay ? (
          <WiDaySnowThunderstorm size={iconSize} color={iconColor} />
        ) : (
          <WiNightSnowThunderstorm size={iconSize} color={iconColor} />
        )

      // Thunderstorm with hail
      case 96:
      case 99:
        return <WiThunderstorm size={iconSize} color={iconColor} />

      default:
        return isDay ? (
          <WiDaySunny size={iconSize} color={iconColor} />
        ) : (
          <WiNightClear size={iconSize} color={iconColor} />
        )
    }
  }

  return (
    <div
      className={cn('skeleton-bg flex max-w-[500px] flex-col gap-4 rounded-2xl p-4', {
        'bg-blue-400': isDay && weatherAtLocation.current.temperature_2m > 0,
        'bg-indigo-900': !isDay && weatherAtLocation.current.temperature_2m > 0,
        'bg-slate-700': weatherAtLocation.current.temperature_2m <= 0,
        'bg-slate-800': weatherAtLocation.current.temperature_2m <= -10
      })}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <div className="flex items-center justify-center">
            {getWeatherIcon(
              isDay,
              weatherAtLocation.current.weather_code,
              weatherAtLocation.current.temperature_2m
            )}
          </div>
          <div className="text-4xl font-medium text-blue-50">
            {n(weatherAtLocation.current.temperature_2m)}
            {weatherAtLocation.current_units.temperature_2m}
          </div>
        </div>
        <div className="text-blue-50">{`H:${n(currentHigh)}° L:${n(currentLow)}°`}</div>
      </div>

      <div className="mt-4 flex justify-between">
        {displayTimes.map((time: string, index: number) => (
          <div key={time.toString()} className="flex flex-col items-center gap-1">
            <div className="text-xs text-blue-100">{format(new Date(time), 'ha')}</div>
            <div className="flex items-center justify-center">
              {getWeatherIcon(
                isWithinInterval(new Date(time), {
                  start: new Date(weatherAtLocation.daily.sunrise[0]),
                  end: new Date(weatherAtLocation.daily.sunset[0])
                }),
                displayWeatherCodes[index],
                displayTemperatures[index]
              )}
            </div>
            <div className="text-sm text-blue-100">{n(displayTemperatures[index])}°</div>
          </div>
        ))}
      </div>
    </div>
  )
}
